import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Stage, Layer, Text, Rect, Circle, Line, Transformer, Image as KonvaImage } from 'react-konva';
import { Download, Type, Square, Circle as CircleIcon, Minus, RotateCcw, Palette, Upload, Image as ImageIcon } from 'lucide-react';
import useImage from 'use-image';

// Component for rendering images in Konva
const ImageElement = ({ imageData, ...props }) => {
  const [image] = useImage(imageData.src, 'Anonymous');
  
  return <KonvaImage image={image} {...props} />;
};

const KonvaLabelDesigner = ({ onDesignChange, initialWidth = 400, initialHeight = 300 }) => {
  // Canvas state
  const [stageSize] = useState({ width: initialWidth, height: initialHeight });
  const [elements, setElements] = useState([
    {
      id: 'title',
      type: 'text',
      x: 50,
      y: 40,
      text: 'Il Tuo Amaro',
      fontSize: 28,
      fontFamily: 'Arial',
      fill: '#8B4513',
      fontStyle: 'bold'
    },
    {
      id: 'subtitle', 
      type: 'text',
      x: 50,
      y: 80,
      text: 'Liquore Artigianale',
      fontSize: 16,
      fontFamily: 'Arial',
      fill: '#CD853F'
    },
    {
      id: 'decorLine',
      type: 'rect',
      x: 50,
      y: 120,
      width: 300,
      height: 2,
      fill: '#D2691E'
    }
  ]);

  // Selection state
  const [selectedId, setSelectedId] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  
  // UI state
  const [activeColor, setActiveColor] = useState('#8B4513');
  const [activeFontSize, setActiveFontSize] = useState(20);
  const [uploadedImages, setUploadedImages] = useState({});
  
  // Refs
  const stageRef = useRef();
  const transformerRef = useRef();
  const fileInputRef = useRef();

  // Colors palette for liqueur labels
  const labelColors = [
    '#8B4513', '#D2691E', '#CD853F', '#F4A460', '#DEB887', '#BC8F8F',
    '#A0522D', '#D2B48C', '#F5DEB3', '#FFEFD5', '#FFE4B5', '#MOCCASIN',
    '#000000', '#FFFFFF', '#FF6B35', '#F7931E'
  ];

  // Font options
  const fontOptions = ['Arial', 'Times New Roman', 'Georgia', 'Verdana', 'Helvetica', 'Cursive'];

  // Update transformer when selection changes
  useEffect(() => {
    if (selectedId && transformerRef.current) {
      const stage = stageRef.current;
      const selectedNode = stage.findOne('#' + selectedId);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.getLayer().batchDraw();
      }
    }
  }, [selectedId]);

  // Handle selection
  const handleStageClick = useCallback((e) => {
    if (e.target === e.target.getStage()) {
      setSelectedId(null);
      setSelectedElement(null);
      return;
    }

    const clickedId = e.target.id();
    setSelectedId(clickedId);
    setSelectedElement(elements.find(el => el.id === clickedId));
  }, [elements]);

  // Handle element changes
  const handleElementChange = useCallback((id, newAttrs) => {
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, ...newAttrs } : el
    ));
    
    // Update selected element if it's the one being changed
    if (selectedId === id) {
      setSelectedElement(prev => ({ ...prev, ...newAttrs }));
    }
  }, [selectedId]);

  // Add new text element
  const addTextElement = useCallback(() => {
    const newId = `text_${Date.now()}`;
    const newElement = {
      id: newId,
      type: 'text',
      x: Math.random() * (stageSize.width - 100) + 50,
      y: Math.random() * (stageSize.height - 50) + 50,
      text: 'Nuovo Testo',
      fontSize: activeFontSize,
      fontFamily: 'Arial',
      fill: activeColor
    };
    
    setElements(prev => [...prev, newElement]);
    setSelectedId(newId);
    setSelectedElement(newElement);
  }, [activeColor, activeFontSize, stageSize]);

  // Add shape elements
  const addRectElement = useCallback(() => {
    const newId = `rect_${Date.now()}`;
    const newElement = {
      id: newId,
      type: 'rect',
      x: Math.random() * (stageSize.width - 100) + 50,
      y: Math.random() * (stageSize.height - 50) + 50,
      width: 100,
      height: 60,
      fill: activeColor,
      stroke: '#000000',
      strokeWidth: 1
    };
    
    setElements(prev => [...prev, newElement]);
    setSelectedId(newId);
    setSelectedElement(newElement);
  }, [activeColor, stageSize]);

  const addCircleElement = useCallback(() => {
    const newId = `circle_${Date.now()}`;
    const newElement = {
      id: newId,
      type: 'circle',
      x: Math.random() * (stageSize.width - 100) + 50,
      y: Math.random() * (stageSize.height - 50) + 50,
      radius: 30,
      fill: activeColor,
      stroke: '#000000',
      strokeWidth: 1
    };
    
    setElements(prev => [...prev, newElement]);
    setSelectedId(newId);
    setSelectedElement(newElement);
  }, [activeColor, stageSize]);

  // Add image element
  const handleImageUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Per favore seleziona un file immagine valido');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Il file è troppo grande. Massimo 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      const newId = `image_${Date.now()}`;
      
      // Create image element to get dimensions
      const img = new Image();
      img.onload = () => {
        // Calculate scaled dimensions to fit canvas
        let { width, height } = img;
        const maxWidth = stageSize.width * 0.4; // Max 40% of canvas width
        const maxHeight = stageSize.height * 0.4; // Max 40% of canvas height
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        const newElement = {
          id: newId,
          type: 'image',
          x: Math.random() * (stageSize.width - width) + 50,
          y: Math.random() * (stageSize.height - height) + 50,
          width: width,
          height: height,
          src: imageUrl,
          originalWidth: img.width,
          originalHeight: img.height
        };

        setElements(prev => [...prev, newElement]);
        setUploadedImages(prev => ({ ...prev, [newId]: imageUrl }));
        setSelectedId(newId);
        setSelectedElement(newElement);
      };
      img.src = imageUrl;
    };
    reader.readAsDataURL(file);

    // Reset file input
    event.target.value = '';
  }, [stageSize]);

  const triggerImageUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Add sample images for quick testing
  const addSampleImage = useCallback((type) => {
    const sampleImages = {
      grapes: 'https://images.unsplash.com/photo-1537640538966-79f369143f8f?w=200&h=200&fit=crop',
      leaf: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=200&h=200&fit=crop', 
      bottle: 'https://images.unsplash.com/photo-1569529465841-dfecdab7503b?w=200&h=200&fit=crop',
      logo: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSI0MCIgZmlsbD0iIzhCNDUxMyIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+TG9nbzwvdGV4dD48L3N2Zz4='
    };

    const imageUrl = sampleImages[type];
    if (!imageUrl) return;

    const newId = `sample_${type}_${Date.now()}`;
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const maxSize = 80; // Max size for sample images
      let { width, height } = img;
      
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      const newElement = {
        id: newId,
        type: 'image',
        x: Math.random() * (stageSize.width - width) + 50,
        y: Math.random() * (stageSize.height - height) + 50,
        width: width,
        height: height,
        src: imageUrl,
        originalWidth: img.width,
        originalHeight: img.height
      };

      setElements(prev => [...prev, newElement]);
      setSelectedId(newId);
      setSelectedElement(newElement);
    };
    img.src = imageUrl;
  }, [stageSize]);

  // Delete selected element
  const deleteSelected = useCallback(() => {
    if (selectedId) {
      setElements(prev => prev.filter(el => el.id !== selectedId));
      setSelectedId(null);
      setSelectedElement(null);
    }
  }, [selectedId]);

  // Clear all elements
  const clearAll = useCallback(() => {
    setElements([]);
    setSelectedId(null);
    setSelectedElement(null);
  }, []);

  // Export design
  const exportDesign = useCallback(() => {
    if (stageRef.current) {
      const dataURL = stageRef.current.toDataURL({
        mimeType: 'image/png',
        quality: 1,
        pixelRatio: 2
      });
      
      const designData = {
        elements: elements,
        stageSize: stageSize,
        timestamp: new Date().toISOString()
      };

      if (onDesignChange) {
        onDesignChange({
          imageDataURL: dataURL,
          designData: JSON.stringify(designData),
          timestamp: new Date().toISOString()
        });
      }

      // Auto download
      const link = document.createElement('a');
      link.download = 'etichetta-personalizzata.png';
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [elements, stageSize, onDesignChange]);

  // Update selected element property
  const updateSelectedProperty = useCallback((property, value) => {
    if (selectedElement) {
      handleElementChange(selectedElement.id, { [property]: value });
    }
  }, [selectedElement, handleElementChange]);

  // Emit design changes
  useEffect(() => {
    if (onDesignChange && elements.length > 0) {
      const designData = {
        elements: elements,
        stageSize: stageSize,
        timestamp: new Date().toISOString()
      };

      // Delayed export to avoid too many calls
      const timeoutId = setTimeout(() => {
        if (stageRef.current) {
          const dataURL = stageRef.current.toDataURL({
            mimeType: 'image/png',
            quality: 0.8,
            pixelRatio: 1
          });
          
          onDesignChange({
            imageDataURL: dataURL,
            designData: JSON.stringify(designData),
            timestamp: new Date().toISOString()
          });
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [elements, stageSize, onDesignChange]);

  return (
    <div className="konva-label-designer">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Designer Etichetta Professionale
        </h3>
        <p className="text-sm text-gray-600">
          Designer avanzato con KonvaJS per etichette personalizzate
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Tools Panel */}
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">🛠️ Strumenti</h4>
            <div className="space-y-2">
              <button
                onClick={addTextElement}
                className="w-full flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                <Type className="w-4 h-4 mr-2" />
                Aggiungi Testo
              </button>
              
              <button
                onClick={addRectElement}
                className="w-full flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                <Square className="w-4 h-4 mr-2" />
                Aggiungi Rettangolo
              </button>
              
              <button
                onClick={addCircleElement}
                className="w-full flex items-center px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
              >
                <CircleIcon className="w-4 h-4 mr-2" />
                Aggiungi Cerchio
              </button>
              
              <button
                onClick={triggerImageUpload}
                className="w-full flex items-center px-3 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Carica Immagine
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Color Picker */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">🎨 Colori</h4>
            <div className="grid grid-cols-4 gap-1">
              {labelColors.map((color, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setActiveColor(color);
                    if (selectedElement && selectedElement.type !== 'image') {
                      updateSelectedProperty('fill', color);
                    }
                  }}
                  className={`w-8 h-8 rounded border-2 transition-all ${
                    activeColor === color ? 'border-gray-800 scale-110' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* Selected Element Controls */}
          {selectedElement && (
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">⚙️ Proprietà</h4>
              
              {selectedElement.type === 'text' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Testo
                    </label>
                    <input
                      type="text"
                      value={selectedElement.text || ''}
                      onChange={(e) => updateSelectedProperty('text', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Dimensione Font
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="72"
                      value={selectedElement.fontSize || 20}
                      onChange={(e) => updateSelectedProperty('fontSize', parseInt(e.target.value))}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{selectedElement.fontSize}px</span>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Font
                    </label>
                    <select
                      value={selectedElement.fontFamily || 'Arial'}
                      onChange={(e) => updateSelectedProperty('fontFamily', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {fontOptions.map(font => (
                        <option key={font} value={font}>{font}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {selectedElement.type === 'image' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Opacità
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={selectedElement.opacity || 1}
                      onChange={(e) => updateSelectedProperty('opacity', parseFloat(e.target.value))}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{Math.round((selectedElement.opacity || 1) * 100)}%</span>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
                    <strong>Dimensioni originali:</strong><br/>
                    {selectedElement.originalWidth}x{selectedElement.originalHeight}px<br/>
                    <strong>Dimensioni attuali:</strong><br/>
                    {Math.round(selectedElement.width)}x{Math.round(selectedElement.height)}px
                  </div>
                </div>
              )}

              {(selectedElement.type === 'rect' || selectedElement.type === 'circle') && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Opacità
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={selectedElement.opacity || 1}
                      onChange={(e) => updateSelectedProperty('opacity', parseFloat(e.target.value))}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{Math.round((selectedElement.opacity || 1) * 100)}%</span>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Bordo
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={selectedElement.strokeWidth || 1}
                      onChange={(e) => updateSelectedProperty('strokeWidth', parseInt(e.target.value))}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{selectedElement.strokeWidth || 1}px</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="border-t pt-4 space-y-2">
            <button
              onClick={deleteSelected}
              disabled={!selectedId}
              className="w-full flex items-center px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Minus className="w-4 h-4 mr-2" />
              Elimina Selezionato
            </button>
            
            <button
              onClick={clearAll}
              className="w-full flex items-center px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Cancella Tutto
            </button>
            
            <button
              onClick={exportDesign}
              className="w-full flex items-center px-3 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta PNG
            </button>
          </div>

          {/* Quick Image Examples */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">🖼️ Immagini di Esempio</h4>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => addSampleImage('grapes')}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                🍇 Uva
              </button>
              <button
                onClick={() => addSampleImage('leaf')}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                🍃 Foglia
              </button>
              <button
                onClick={() => addSampleImage('bottle')}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                🍾 Bottiglia
              </button>
              <button
                onClick={() => addSampleImage('logo')}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                🏷️ Logo
              </button>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-700">
            ✅ Designer KonvaJS Ready!<br/>
            📐 {stageSize.width}x{stageSize.height}px<br/>
            📦 {elements.length} elementi<br/>
            🖼️ Immagini supportate: JPG, PNG, SVG, WEBP
          </div>
        </div>

        {/* Canvas Area */}
        <div className="xl:col-span-3">
          <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white relative">
            <Stage
              ref={stageRef}
              width={stageSize.width}
              height={stageSize.height}
              onMouseDown={handleStageClick}
              onTouchStart={handleStageClick}
              style={{ cursor: selectedId ? 'move' : 'default' }}
            >
              <Layer>
                {/* Background */}
                <Rect
                  x={0}
                  y={0}
                  width={stageSize.width}
                  height={stageSize.height}
                  fill="#ffffff"
                  stroke="#e5e7eb"
                  strokeWidth={1}
                />

                {/* Render all elements */}
                {elements.map((element) => {
                  const commonProps = {
                    key: element.id,
                    id: element.id,
                    x: element.x,
                    y: element.y,
                    opacity: element.opacity !== undefined ? element.opacity : 1,
                    draggable: true,
                    onDragEnd: (e) => {
                      handleElementChange(element.id, {
                        x: e.target.x(),
                        y: e.target.y()
                      });
                    },
                    onClick: () => {
                      setSelectedId(element.id);
                      setSelectedElement(element);
                    }
                  };

                  switch (element.type) {
                    case 'text':
                      return (
                        <Text
                          {...commonProps}
                          text={element.text}
                          fontSize={element.fontSize}
                          fontFamily={element.fontFamily}
                          fill={element.fill}
                          fontStyle={element.fontStyle}
                        />
                      );
                    case 'rect':
                      return (
                        <Rect
                          {...commonProps}
                          width={element.width}
                          height={element.height}
                          fill={element.fill}
                          stroke={element.stroke}
                          strokeWidth={element.strokeWidth}
                        />
                      );
                    case 'circle':
                      return (
                        <Circle
                          {...commonProps}
                          radius={element.radius}
                          fill={element.fill}
                          stroke={element.stroke}
                          strokeWidth={element.strokeWidth}
                        />
                      );
                    case 'image':
                      return (
                        <ImageElement
                          {...commonProps}
                          imageData={{ src: element.src }}
                          width={element.width}
                          height={element.height}
                        />
                      );
                    default:
                      return null;
                  }
                })}

                {/* Transformer for selected element */}
                {selectedId && (
                  <Transformer
                    ref={transformerRef}
                    boundBoxFunc={(oldBox, newBox) => {
                      // Limit resize to positive values
                      if (newBox.width < 5 || newBox.height < 5) {
                        return oldBox;
                      }
                      return newBox;
                    }}
                    onTransformEnd={(e) => {
                      const node = e.target;
                      const scaleX = node.scaleX();
                      const scaleY = node.scaleY();
                      
                      // Update element dimensions
                      const updates = {
                        x: node.x(),
                        y: node.y(),
                        rotation: node.rotation()
                      };

                      if (selectedElement.type === 'text') {
                        updates.fontSize = selectedElement.fontSize * scaleX;
                      } else if (selectedElement.type === 'rect') {
                        updates.width = selectedElement.width * scaleX;
                        updates.height = selectedElement.height * scaleY;
                      } else if (selectedElement.type === 'circle') {
                        updates.radius = selectedElement.radius * scaleX;
                      } else if (selectedElement.type === 'image') {
                        updates.width = selectedElement.width * scaleX;
                        updates.height = selectedElement.height * scaleY;
                      }

                      handleElementChange(selectedId, updates);
                      
                      // Reset scale
                      node.scaleX(1);
                      node.scaleY(1);
                    }}
                  />
                )}
              </Layer>
            </Stage>
          </div>
          
          <div className="mt-2 text-xs text-gray-500 text-center">
            Clicca per selezionare • Trascina per spostare • Usa i controlli per ridimensionare
          </div>
        </div>
      </div>
    </div>
  );
};

export default KonvaLabelDesigner;